import React from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Plus, X } from 'lucide-react';

interface WorkOrderComponentLinkerProps {
    componentKeys: string[];
    componentDisplayName: (key: string) => string;
    onRemove: (key: string) => void;
    onOpenPicker: () => void;
    /** A building must be selected before components can be linked. */
    canPick: boolean;
    /** The full building model is still loading. */
    loading: boolean;
    /** The full building model has loaded and the picker can open. */
    ready: boolean;
    label?: string;
}

/**
 * The "link components to this work order" block: the list of currently linked
 * components with remove buttons, plus the button that opens the component picker.
 * Shared by the create and edit work-order modals.
 */
export const WorkOrderComponentLinker: React.FC<WorkOrderComponentLinkerProps> = ({
    componentKeys,
    componentDisplayName,
    onRemove,
    onOpenPicker,
    canPick,
    loading,
    ready,
    label = 'Komponenter (valfritt)',
}) => (
    <Form.Group className="mb-3">
        <Form.Label>{label}</Form.Label>
        {componentKeys.length > 0 ? (
            <div className="d-flex flex-column gap-1 mb-2">
                {componentKeys.map(key => (
                    <div key={key} className="d-flex align-items-center gap-2">
                        <span>{componentDisplayName(key)}</span>
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-danger"
                            onClick={() => onRemove(key)}
                            aria-label="Ta bort komponent"
                        >
                            <X size={16} />
                        </Button>
                    </div>
                ))}
            </div>
        ) : (
            <p className="text-muted small mb-2">Inga komponenter kopplade</p>
        )}
        <Button
            variant="outline-secondary"
            size="sm"
            onClick={onOpenPicker}
            disabled={!canPick || loading || !ready}
        >
            {loading ? (
                <><Spinner animation="border" size="sm" className="me-1" />Laddar...</>
            ) : (
                <><Plus size={14} className="me-1" />Lägg till komponenter</>
            )}
        </Button>
        <Form.Text className="text-muted d-block">
            {canPick
                ? 'Lämna tomt om ärendet inte gäller specifika komponenter'
                : 'Välj en byggnad först för att koppla komponenter'}
        </Form.Text>
    </Form.Group>
);
